import dayjs from "dayjs";
import { flatMap, isEmpty } from "lodash";
import { ComponentType, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import { useNavigate, useParams } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import GreenTimerSVG from "@krea/customer-features/images/svg/green-timer.svg";
import TimerSVG from "@krea/customer-features/images/svg/timer.svg";
import { settings } from "@krea/customer-features/settings";
import { AdvisorCTABanner } from "@krea/customer-features/shared-components/advisor-cta-banner";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import LendersList, {
  ILendersBids,
} from "@krea/customer-features/shared-components/bid-dashboards/company-loan-bids";
import LendersFactoringBids from "@krea/customer-features/shared-components/bid-dashboards/factoring-bids";
import LendersLeasingBids from "@krea/customer-features/shared-components/bid-dashboards/leasing-bids";
import LendersRealEstateBids from "@krea/customer-features/shared-components/bid-dashboards/real-estate-bids";
import RevolvingCreditBids from "@krea/customer-features/shared-components/bid-dashboards/revolving-credit-bids";
import SelectedBidItem from "@krea/customer-features/shared-components/bid-dashboards/selected-bid";
import BidsHeader from "@krea/customer-features/shared-components/bids-header";
import Container from "@krea/customer-features/shared-components/container";
import NavigateAction from "@krea/customer-features/shared-components/navigate-action";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { ToastWithContactPerson } from "@krea/customer-features/shared-components/toast/ToastWithContactPerson";
import { IFieldRequest } from "@krea/customer-features/types/fieldRequest";
import {
  IBids,
  ICompanyLoanBid,
  IInvoicePurchaseBid,
  ILeasingBid,
  ILenderResponse,
  IRealEstateBid,
  IResolvedBid,
  IRevolvingCreditBid,
} from "@krea/customer-features/types/lenderResponse";
import {
  CustomerPreferredRateEnum,
  LoanApplicationProduct,
  LoanApplicationStates,
} from "@krea/customer-features/types/loanApplication";
import { APPLICATION_NAME, BID_TYPE } from "@krea/customer-features/utils";
import { getGoodAlternativeLenderResponseIDs } from "@krea/customer-features/utils/goodAlternativeLenderResponseIDs";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { Alert } from "@krea/shared/design-system/alerts/Alert";

import { ISelectLenderResponseMutationResult } from "./types";

interface IBidDashboardProps {
  lenderResponsesData?: IBids;
  selectedLenderResponse?: ILenderResponse | null;
  state: LoanApplicationStates;
  product: LoanApplicationProduct;
  biddingEndsAt: string;
  customerPreferredRate: CustomerPreferredRateEnum;
  isLoading: boolean;
  selectLenderResponseMutation: ISelectLenderResponseMutationResult;
}

const BidDashboard = ({
  lenderResponsesData,
  selectedLenderResponse,
  state,
  product,
  biddingEndsAt,
  customerPreferredRate,
  isLoading,
  selectLenderResponseMutation,
}: IBidDashboardProps) => {
  const isMobile = useMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loanApplicationID } = useParams();

  const { recommendedLenderResponse } = lenderResponsesData ?? {};

  const lenderBids = {
    companyLoanBids: lenderResponsesData?.companyLoanBids ?? [],
    invoicePurchaseBids: lenderResponsesData?.invoicePurchaseBids ?? [],
    realEstateBids: lenderResponsesData?.realEstateBids ?? [],
    leasingBids: lenderResponsesData?.leasingBids ?? [],
    revolvingCreditBids: lenderResponsesData?.revolvingCreditBids ?? [],
  };

  const biddingClosed = biddingEndsAt
    ? dayjs(biddingEndsAt).isBefore(dayjs())
    : false;

  const lenderMillisecondsLeft =
    dayjs(biddingEndsAt).valueOf() - dayjs().valueOf();
  const lenderHoursLeft = Math.ceil(lenderMillisecondsLeft / (1000 * 60 * 60));

  const hasBids = flatMap(lenderBids).length > 0;
  const {
    companyLoanBids,
    invoicePurchaseBids,
    realEstateBids,
    leasingBids,
    revolvingCreditBids,
  } = lenderBids;

  const selectedLenderResponseID = selectedLenderResponse?.id;
  const recommendedLenderResponseID = recommendedLenderResponse?.id;

  const allBids = hasBids
    ? [
        companyLoanBids,
        invoicePurchaseBids,
        realEstateBids,
        leasingBids,
        revolvingCreditBids,
      ].flat()
    : [];

  let goodAlternativeLenderResponseIDs;

  if (product === LoanApplicationProduct.FACTORING) {
    goodAlternativeLenderResponseIDs = getGoodAlternativeLenderResponseIDs(
      invoicePurchaseBids,
      customerPreferredRate,
      selectedLenderResponseID,
      recommendedLenderResponseID,
    );
  } else if (product === LoanApplicationProduct.LEASING) {
    goodAlternativeLenderResponseIDs = getGoodAlternativeLenderResponseIDs(
      leasingBids,
      customerPreferredRate,
      selectedLenderResponseID,
      recommendedLenderResponseID,
    );
  } else {
    goodAlternativeLenderResponseIDs = getGoodAlternativeLenderResponseIDs(
      allBids,
      customerPreferredRate,
      selectedLenderResponseID,
      recommendedLenderResponseID,
    );
  }

  const isPartnerWeb = settings.appName === APPLICATION_NAME.PARTNER_WEB;
  const isCustomerWeb = settings.appName === APPLICATION_NAME.CUSTOMER_WEB;

  const handleNavigation = useCallback(() => {
    if (isPartnerWeb) {
      navigate(`/loan-applications/${loanApplicationID}/selected`);
    } else if (isCustomerWeb) {
      navigate("/selected-lender-response");
    }
  }, [isPartnerWeb, isCustomerWeb, navigate, loanApplicationID]);

  const handleSelectLenderResponse = useCallback(
    (lenderResponse: ILenderResponse) => {
      const isCurrentLenderResponseSelected =
        selectedLenderResponseID === lenderResponse.id;

      if (isCurrentLenderResponseSelected) {
        handleNavigation();
      } else {
        selectLenderResponseMutation.mutate(
          {
            lenderResponseID: lenderResponse.id,
          },
          {
            onSuccess: () => {
              handleNavigation();
            },
          },
        );
      }
    },
    [selectedLenderResponseID, handleNavigation, selectLenderResponseMutation],
  );

  const getBidsListByBidType = useCallback(
    (
      bidsList:
        | ICompanyLoanBid[]
        | IRealEstateBid[]
        | ILeasingBid[]
        | IRevolvingCreditBid[]
        | IInvoicePurchaseBid[],
      Component: ComponentType<ILendersBids>,
      bidType: BID_TYPE,
      title: string,
    ) => {
      return !isEmpty(bidsList) ? (
        <Component
          key={bidType}
          title={title}
          selectedLenderResponseID={selectedLenderResponseID}
          onSelectLenderResponseClick={(lenderResponse: ILenderResponse) => {
            handleSelectLenderResponse(lenderResponse);
          }}
          isSelecting={selectLenderResponseMutation.isLoading}
          recommendedLenderResponseID={recommendedLenderResponseID}
          goodAlternativeLenderResponseIDs={goodAlternativeLenderResponseIDs}
        />
      ) : null;
    },
    [
      handleSelectLenderResponse,
      selectedLenderResponseID,
      goodAlternativeLenderResponseIDs,
      recommendedLenderResponse,
    ],
  );

  const bidsListLoan = useMemo(
    () =>
      getBidsListByBidType(
        companyLoanBids,
        LendersList,
        BID_TYPE.COMPANY_LOAN,
        t("dash.loanBids.heading"),
      ),
    [t, getBidsListByBidType, companyLoanBids],
  );

  const bidsListFactoring = useMemo(
    () =>
      getBidsListByBidType(
        invoicePurchaseBids,
        LendersFactoringBids,
        BID_TYPE.FACTORING,
        t("dash.factoringBids.heading"),
      ),
    [t, getBidsListByBidType, invoicePurchaseBids],
  );

  const bidsListRealEstate = useMemo(
    () =>
      getBidsListByBidType(
        realEstateBids,
        LendersRealEstateBids,
        BID_TYPE.REAL_ESTATE,
        t("dash.realEstateBids.heading"),
      ),
    [t, getBidsListByBidType, realEstateBids],
  );

  const bidsListLeasing = useMemo(
    () =>
      getBidsListByBidType(
        leasingBids,
        LendersLeasingBids,
        BID_TYPE.LEASING,
        t("dash.leasingBids.heading"),
      ),
    [t, getBidsListByBidType, leasingBids],
  );

  const bidsListRevolvingCredit = useMemo(
    () =>
      getBidsListByBidType(
        revolvingCreditBids,
        RevolvingCreditBids,
        BID_TYPE.REVOLVING_CREDIT,
        t("dash.revolvingCreditBids.heading"),
      ),
    [t, getBidsListByBidType, revolvingCreditBids],
  );

  const getSortedListOfBids = () => {
    if (product === LoanApplicationProduct.FACTORING) {
      return [
        bidsListFactoring,
        bidsListLoan,
        bidsListRevolvingCredit,
        bidsListRealEstate,
        bidsListLeasing,
      ];
    } else if (product === LoanApplicationProduct.REAL_ESTATE) {
      return [
        bidsListRealEstate,
        bidsListLoan,
        bidsListRevolvingCredit,
        bidsListFactoring,
        bidsListLeasing,
      ];
    } else if (product === LoanApplicationProduct.LEASING) {
      return [
        bidsListLeasing,
        bidsListLoan,
        bidsListRevolvingCredit,
        bidsListRealEstate,
        bidsListFactoring,
      ];
    }

    // COMPANY_LOAN
    return [
      bidsListLoan,
      bidsListRevolvingCredit,
      bidsListFactoring,
      bidsListRealEstate,
      bidsListLeasing,
    ];
  };

  const sortedListOfBids = getSortedListOfBids();

  const renderTitle = () => {
    let title;

    if (state === LoanApplicationStates.OPEN_FOR_BIDS) {
      if (hasBids) {
        title = t("dash.headingBidsReceived"); // Choose from offers
      } else if (biddingClosed) {
        title = t("dash.headingNoBidsReceived"); // You have not received any offers so far
      } else {
        title = t("dash.heading"); // Your offers are on the way
      }
    } else if (state === LoanApplicationStates.NO_BIDS_RECEIVED) {
      title = t("dash.headingNoBidsReceived"); // You have not received any offers so far
    } else if (
      state === LoanApplicationStates.BID_SELECTED ||
      state === LoanApplicationStates.PAYOUT_RECEIVED_BY_CUSTOMER
    ) {
      title = t("dash.headingBidSelected"); // Your offers
    } else {
      return null;
    }

    return (
      <>
        <div className="lg:tw-flex lg:tw-justify-between">
          {isMobile ? <h3>{title}</h3> : <h1>{title}</h1>}

          {biddingEndsAt && (
            <div className="tw-mb-3 tw-inline-block lg:tw-mb-0">
              <div className="tw-flex tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-py-2 tw-pl-1 tw-pr-2">
                <SVG
                  className="tw-h-[18px] md:tw-mr-px md:tw-mt-px md:tw-h-[20px]"
                  src={
                    !biddingClosed && lenderHoursLeft ? GreenTimerSVG : TimerSVG
                  }
                />
                <p className="tw-m-0 tw-text-xs md:tw-text-sm">
                  {!biddingClosed && lenderHoursLeft
                    ? t("dash.biddingOpen", { lenderHoursLeft })
                    : t("dash.biddingClosed")}
                </p>
              </div>
            </div>
          )}
        </div>
        {state === LoanApplicationStates.OPEN_FOR_BIDS &&
          !hasBids &&
          !biddingClosed && (
            <p style={isMobile ? { fontSize: "16px" } : { fontSize: "18px" }}>
              {t("dash.applicationHasBeenSent")}
            </p>
          )}
      </>
    );
  };

  let selectedBid: IResolvedBid | undefined;
  let selectedBidType: BID_TYPE = BID_TYPE.COMPANY_LOAN; // default value

  for (const [key, bids] of Object.entries(lenderBids) as [
    string,
    IResolvedBid[],
  ][]) {
    selectedBid = bids.find(
      (
        bid:
          | ICompanyLoanBid
          | IInvoicePurchaseBid
          | IRealEstateBid
          | IRevolvingCreditBid
          | ILeasingBid,
      ) => bid.lenderResponse.id === selectedLenderResponseID,
    );

    if (bids.length > 0) {
      if (selectedBid) {
        switch (key) {
          case "companyLoanBids":
            selectedBidType = BID_TYPE.COMPANY_LOAN;
            break;
          case "invoicePurchaseBids":
            selectedBidType = BID_TYPE.FACTORING;
            break;
          case "realEstateBids":
            selectedBidType = BID_TYPE.REAL_ESTATE;
            break;
          case "leasingBids":
            selectedBidType = BID_TYPE.LEASING;
            break;
          case "revolvingCreditBids":
            selectedBidType = BID_TYPE.REVOLVING_CREDIT;
            break;
        }
        break;
      }
    }
  }

  const lenderResponsesCount = flatMap(lenderBids).length;
  const unselectedBidsCount = !isEmpty(selectedLenderResponse)
    ? lenderResponsesCount - 1
    : lenderResponsesCount;

  return (
    <>
      <LayoutOneColumn wide>
        {isLoading ? (
          <div
            className="tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-white"
            style={{ minHeight: "400px" }}
          >
            <Preloader className="tw-text-primary" size="xl" />
          </div>
        ) : (
          <>
            <Container>
              {renderTitle()}

              <BidsHeader />
              {state === LoanApplicationStates.OPEN_FOR_BIDS &&
                lenderResponsesCount > 0 && (
                  <Alert variant="infoAlert" isClosable>
                    {t("dash.noBindingOffersBanner")}
                  </Alert>
                )}
            </Container>
            {selectedBid && (
              <div className="tw-mx-auto tw-w-full tw-px-6 sm:tw-max-w-[640px] md:tw-max-w-[720px] md:tw-px-0 lg:tw-max-w-[750px] xl:tw-w-full xl:tw-max-w-[1320px] xl:tw-px-10">
                <div className="tw-mb-10">
                  <SelectedBidItem
                    onSelectLenderResponseClick={(lenderResponse) => {
                      handleSelectLenderResponse(lenderResponse);
                    }}
                    bid={selectedBid}
                    bidType={selectedBidType}
                  />
                </div>

                {unselectedBidsCount > 0 && (
                  <ToastWithContactPerson
                    innerText={{
                      title: t(
                        "recommendedSelect.proceedWithAnotherOfferToast.heading",
                      ),
                      subtitle: t(
                        "recommendedSelect.proceedWithAnotherOfferToast.subheading",
                      ),
                    }}
                  />
                )}
              </div>
            )}

            {![
              LoanApplicationStates.NO_BIDS_RECEIVED,
              LoanApplicationStates.BIDS_REJECTED_BY_CUSTOMER,
            ].includes(state) ? (
              <>
                <div className="tw-mb-8 xl:tw-mb-12">
                  {sortedListOfBids.map((bids) => bids)}
                </div>
              </>
            ) : null}

            {isCustomerWeb &&
              state !== LoanApplicationStates.CREATED &&
              state !== LoanApplicationStates.REJECTED &&
              !(
                state === LoanApplicationStates.OPEN_FOR_BIDS &&
                !hasBids &&
                biddingClosed
              ) && (
                <Container
                  className={twMerge({
                    "tw--mt-10":
                      !hasBids && state === LoanApplicationStates.OPEN_FOR_BIDS,
                    "tw-mt-5":
                      !hasBids && state !== LoanApplicationStates.OPEN_FOR_BIDS,
                    "tw--mt-7": hasBids && !isMobile,
                  })}
                >
                  <AdvisorCTABanner />
                </Container>
              )}

            {state === LoanApplicationStates.OPEN_FOR_BIDS ? (
              <Container>
                <NavigateAction
                  className="tw-mt-4"
                  title={t("dash.changeInformationForApplication.title")}
                  button={{
                    text: t("dash.changeInformationForApplication.back"),
                    onClick: () => {
                      if (isCustomerWeb) {
                        navigate(
                          "/direct/improve-application-questions/preview",
                        );
                      } else if (isPartnerWeb) {
                        navigate(
                          `/loan-applications/${loanApplicationID}/preview`,
                        );
                      }
                    },
                  }}
                />
              </Container>
            ) : null}
          </>
        )}

        <div className="tw-pb-6" />
      </LayoutOneColumn>
    </>
  );
};

export default BidDashboard;
